import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import AddInputs from "../../components/Add/AddInputs";
import AddModel from "../../components/Add/AddModel";
import AddBottomButton from "../../components/Add/AddBottomButton";
import { showToast } from "../../components/toastMessage/toastSlice";
import { setStaticData } from "../../store/slices/bulkTransferSlice";
import useApi from "../../services/useApi";
import CustomModal from "../../components/CustomModal";

export default function UpdateWallet() {
  const location = useLocation();
  const editData = location.state?.editData;
  const { getAPI, PostAPI } = useApi();
  const staticData = useSelector((state: { bulkTransfer: { staticData: unknown[] } }) => state.bulkTransfer.staticData);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Efficient type detection using query parameter, fallback to data property check
  const urlType = searchParams.get('type');
  const isRecharge = urlType === 'recharge' || (urlType !== 'wallet-transfer' && editData && (Object.prototype.hasOwnProperty.call(editData, 'phone_number') || Object.prototype.hasOwnProperty.call(editData, 'name')));

  const [inputValues, setInputValues] = useState(
    isRecharge ? {
      phone_number: "",
      country_code: "", // Store country_code separately for API calls
      name: "",
      father_name: "",
      amount: "",
      location: "",
      company_name: "",
      wallet_type: "",
      telecom_name: ""
    } : {
      user_code: "",
      wallet_type: "",
      amount: ""
    }
  );
  const [errors, setErrors] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [childData, setChildData] = useState<any[]>([]); // Store child data from API
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [prevFirstTwoDigits, setPrevFirstTwoDigits] = useState<string>("");

  // Helper function to clean phone number and format country code
  const cleanPhoneData = (phoneNumber: string, countryCode: string) => {
    let cleanPhoneNumber = phoneNumber?.trim() || '';
    let formattedCountryCode = countryCode?.trim() || '';

    // Normalize: remove spaces and plus sign for comparison
    const normalizedPhone = cleanPhoneNumber.replace(/\s+/g, '').replace(/^\+/, '');
    const normalizedCode = formattedCountryCode.replace(/\s+/g, '').replace(/^\+/, '');

    // Remove country code from phone number if accidentally included
    if (normalizedCode && normalizedPhone.startsWith(normalizedCode)) {
      cleanPhoneNumber = normalizedPhone.substring(normalizedCode.length);
    } else {
      cleanPhoneNumber = normalizedPhone;
    }

    // Ensure country code has + prefix
    if (formattedCountryCode && !formattedCountryCode.startsWith('+')) {
      formattedCountryCode = `+${normalizedCode}`;
    } else {
      formattedCountryCode = `+${normalizedCode}`;
    }

    return { cleanPhoneNumber, formattedCountryCode };
  };

  // Debounced API call for MNO detection
  const debouncedApiCall = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (countryCode: string, firstTwoDigits: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          try {
            const res = await getAPI(`merchant-recharge/mno?country_code=${encodeURIComponent('+' + countryCode)}&phone_number=${firstTwoDigits}`);
            if (res?.error) {
              setErrors(prev => ({ ...prev, phone_number: res.data?.data?.message }));
              setInputValues(prev => ({ ...prev, telecom_name: "" }) as typeof prev);
            } else {
              setErrors(prev => ({ ...prev, phone_number: "" }));
              setInputValues(prev => ({ ...prev, telecom_name: res?.data?.data?.name || "" }) as typeof prev);
            }
          } catch (error) {
            console.error('MNO detection error:', error);
            setInputValues(prev => ({ ...prev, telecom_name: "" }) as typeof prev);
          }
        }, 300);
      };
    })(),
    [getAPI]
  );

  const fetchChildAccounts = async (searchTerm = '', page = 1, isLoadMore = false) => {
    try {
      const endpoint = searchTerm
        ? `merchant-recharge/list-admin-childs?search=${encodeURIComponent(searchTerm)}&page_number=${page}`
        : `merchant-recharge/list-admin-childs?page_number=${page}`;
      const response = await getAPI(endpoint);
      if (!response?.error && response?.data?.data) {
        const newOptions = response.data.data.map((child: { first_name: string; last_name: string; child_user_code: string }) =>
          `${child.first_name} ${child.last_name} (${child.child_user_code})`
        );
        
        if (isLoadMore) {
          setUserOptions(prev => [...prev, ...newOptions]);
          setChildData(prev => [...prev, ...response.data.data]);
        } else {
          setUserOptions(newOptions);
          setChildData(response.data.data);
          setCurrentPage(1);
        }
        
        const hasMore = response.data.data.length === 10; // PAGE_SIZE from backend
        setHasMoreUsers(hasMore);
        return { data: { total_count: response.data.total_count || 0 } };
      } else {
        setHasMoreUsers(false);
        return { data: { total_count: 0 } };
      }
    } catch (error) {
      console.error('Error fetching child accounts:', error);
      setHasMoreUsers(false);
      return { data: { total_count: 0 } };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isRecharge) {
      fetchChildAccounts();
    }
  }, []);

  useEffect(() => {
    if (editData) {
      if (isRecharge) {
        // Extract country_code and phone_number from combined display format
        let phoneNumber = "";
        let countryCode = editData.country_code || "";

        // Check if we have phone_number_with_country_code (from list display)
        if (editData.phone_number_with_country_code) {
          // Use the combined format for display in input field
          phoneNumber = editData.phone_number_with_country_code;
          // Extract country code if not already available
          if (!countryCode && phoneNumber.includes(' ')) {
            const parts = phoneNumber.split(' ');
            countryCode = parts[0]; // e.g., "+93"
          }
        } else {
          // Fallback to separate fields
          phoneNumber = editData.phone_number || "";
          // If phone_number contains country_code (format: "+93 1234567890"), split them
          if (phoneNumber.includes(' ') && !countryCode) {
            const parts = phoneNumber.split(' ');
            countryCode = parts[0]; // e.g., "+93"
            phoneNumber = parts.slice(1).join(' '); // e.g., "1234567890"
          }
          // Combine for display in input field
          if (countryCode && phoneNumber) {
            phoneNumber = `${countryCode} ${phoneNumber}`;
          }
        }

        // Ensure country_code has + prefix for API consistency
        if (countryCode && !countryCode.startsWith('+')) {
          countryCode = `+${countryCode}`;
        }

        const rechargeValues = {
          phone_number: phoneNumber, // Store combined phone number with country code for input field display
          country_code: countryCode, // Store country code separately
          name: editData.name || "",
          father_name: editData.father_name || "",
          phone_number_with_country_code: `${countryCode}${phoneNumber}`,
          amount: editData.amount || "",
          location: editData.location || "",
          company_name: editData.company_name || "",
          wallet_type: editData.wallet_type || "",
          telecom_name: editData.telecom_name || ""
        };
        setInputValues(rechargeValues);
      } else {
        // Map storage values to display values for wallet_type
        let displayWalletType = editData.wallet_type || "";
        if (displayWalletType === 'etopup') {
          displayWalletType = 'eTop Up';
        } else if (displayWalletType === 'digital') {
          displayWalletType = 'Digital Wallet';
        }

        const walletValues = {
          user_code: editData.user_code || "",
          wallet_type: displayWalletType,
          amount: editData.amount || ""
        };
        setInputValues(walletValues);
      }
    }
  }, [editData, isRecharge]);

  const handleInputChange = (id: string, value: string) => {
    let processedValue = value;

    // For user_code selection, extract clean user code from display format
    if (id === 'user_code' && !isRecharge && value) {
      const selectedChild = childData.find(child =>
        `${child.first_name} ${child.last_name} (${child.child_user_code})` === value
      );
      if (selectedChild) {
        processedValue = selectedChild.child_user_code; // Store only the clean user code
      }
    }

    // For wallet_type, keep display values in component state
    // Conversion to API values happens during API calls

    setInputValues(prev => ({ ...prev, [id]: processedValue }) as typeof prev);
    // Clear error for this field when user starts typing
    if ((errors as Record<string, string>)[id]) {
      setErrors(prev => ({ ...prev, [id]: '' }));
    }

    // MNO detection for phone number changes (only for recharge)
    if (id === 'phone_number' && isRecharge && value) {
      const phoneWithCountryCode = value.trim();

      // Handle format: 93789873827 (country code + phone number without space)
      if (phoneWithCountryCode.length >= 4) {
        // Assume first 2-3 digits are country code (93 for Afghanistan)
        let countryCodePart = '';
        let phoneWithoutCountryCode = '';

        // Try 2-digit country code first (93)
        if (phoneWithCountryCode.startsWith('93')) {
          countryCodePart = '93';
          phoneWithoutCountryCode = phoneWithCountryCode.substring(2);
        } else if (phoneWithCountryCode.length >= 5) {
          // Try 3-digit country code as fallback
          countryCodePart = phoneWithCountryCode.substring(0, 3);
          phoneWithoutCountryCode = phoneWithCountryCode.substring(3);
        }

        if (countryCodePart && phoneWithoutCountryCode.length >= 2) {
          const firstTwoDigits = phoneWithoutCountryCode.slice(0, 2);

          // Update country_code in inputValues
          setInputValues(prev => ({ ...prev, country_code: `+${countryCodePart}` }) as typeof prev);

          // Trigger API call when first two digits change and are complete
          if (firstTwoDigits.length === 2 && firstTwoDigits !== prevFirstTwoDigits) {
            setPrevFirstTwoDigits(firstTwoDigits);
            debouncedApiCall(countryCodePart, firstTwoDigits);
          }
        }
      }
    }
  };

  const validateFields = () => {
    const fieldErrors: Record<string, string> = {};

    if (isRecharge) {
      // Mandatory fields for recharge
      if (!inputValues.name?.trim()) {
        fieldErrors.name = 'Customer name is required';
      }
      if (!inputValues.phone_number?.trim()) {
        fieldErrors.phone_number = 'Phone number is required';
      } else {
        // Validate phone number length (excluding country code)
        const { cleanPhoneNumber } = cleanPhoneData(inputValues.phone_number, inputValues.country_code);
        if (cleanPhoneNumber.length < 7) {
          fieldErrors.phone_number = 'Invalid phone number';
        }
      }
      if (!inputValues.amount || Number(inputValues.amount) <= 0) {
        fieldErrors.amount = 'Valid recharge amount is required';
      }
      if (!inputValues.wallet_type?.trim()) {
        fieldErrors.wallet_type = 'Wallet type is required';
      } else if (inputValues.wallet_type === 'Digital Wallet') {
        fieldErrors.wallet_type = 'Digital wallet is not available currently';
      }
    } else {
      // Mandatory fields for wallet transfer
      if (!inputValues.user_code?.trim()) {
        fieldErrors.user_code = 'User code is required';
      }
      if (!inputValues.wallet_type?.trim()) {
        fieldErrors.wallet_type = 'Wallet type is required';
      } else if (inputValues.wallet_type === 'Digital Wallet') {
        fieldErrors.wallet_type = 'Digital wallet is not available currently';
      }
      if (!inputValues.amount || Number(inputValues.amount) <= 0) {
        fieldErrors.amount = 'Valid amount is required';
      }
    }

    return fieldErrors;
  };

  const handleSubmit = async () => {
    // Validate mandatory fields first
    const fieldErrors = validateFields();

    // Check for existing errors (including telecom detection errors)
    const hasExistingErrors = Object.values(errors).some(error => error && typeof error === 'string' && error.trim() !== '');

    // If there are validation errors or existing errors, set them and return
    if (Object.keys(fieldErrors).length > 0 || hasExistingErrors) {
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...fieldErrors }));
      }
      return;
    }

    // Clear any existing errors and show confirmation modal
    setErrors({});
    setShowConfirmModal(true);
  };

  const handleConfirmUpdate = async () => {
    setModalLoading(true);

    try {
      let validationData;
      let apiEndpoint;

      if (isRecharge) {
        // Clean phone number and country code for API
        const { cleanPhoneNumber, formattedCountryCode } = cleanPhoneData(inputValues.phone_number!, inputValues.country_code!);

        validationData = {
          recharges: [{
            phone_number: cleanPhoneNumber, // Clean phone number without country code
            country_code: formattedCountryCode, // Country code with + prefix
            name: inputValues.name,
            father_name: inputValues.father_name,
            amount: Number(inputValues.amount),
            location: inputValues.location,
            company_name: inputValues.company_name,
            wallet_type: inputValues.wallet_type === 'eTop Up' ? 'etopup' : 'digital' // Convert display value to API value
          }]
        };
        apiEndpoint = 'merchant-recharge/bulk-mno';
      } else {
        // Prepare wallet transfer data for wallet/validate API - uses 'records' key (matches wallet-transfer.tsx)
        validationData = {
          records: [{
            user_code: inputValues.user_code, // Already clean user code from handleInputChange
            wallet_type: inputValues.wallet_type === 'eTop Up' ? 'etopup' : 'digital', // Convert display to API value
            amount: Number(inputValues.amount)
          }]
        };
        apiEndpoint = 'merchant-recharge/wallet/validate';
      }

      // Call validation API
      const response = await PostAPI(apiEndpoint, validationData);

      if (response?.error) {
        dispatch(showToast({ type: 'error', message: 'Validation failed. Please try again.' }));
        return;
      }

      // For recharge: Check MNO amount limits immediately after API response
      if (isRecharge && response?.data?.results?.[0]) {
        const apiResponseItem = response.data.results[0];
        if (apiResponseItem.data?.min_recharge_amount && apiResponseItem.data?.max_recharge_amount) {
          const amount = Number(inputValues.amount);
          const minAmount = apiResponseItem.data.min_recharge_amount;
          const maxAmount = apiResponseItem.data.max_recharge_amount;

          if (amount < minAmount || amount > maxAmount) {
            // Close modal and show error under amount field
            setShowConfirmModal(false);
            let errorMessage = '';
            if (amount < minAmount) {
              errorMessage = `Amount must be greater than or equal to AFA ${minAmount.toFixed(2)}`;
            } else if (amount > maxAmount) {
              errorMessage = `Maximum amount is AFA ${maxAmount.toFixed(2)}`;
            }
            setErrors(prev => ({
              ...prev,
              amount: errorMessage
            }));
            return;
          }
        }
      }
      // For wallet transfer, check if the user code is valid
      if (!isRecharge && response?.data?.data?.[0]?.status === 'error') {
        if (response?.data?.data?.[0]?.error_message === 'Invalid user')
          setShowConfirmModal(false);
        setErrors(prev => ({
          ...prev,
          user_code: 'Invalid user code'
        }));
        return;
      }

      // Update the static data in list slice with API response data
      if (staticData && Array.isArray(staticData)) {
        const updatedData = (staticData as Record<string, unknown>[]).map((item: Record<string, unknown>) => {
          // Use unique UUID to match the exact record being edited
          if (item.id === editData?.id) {
            if (isRecharge) {
              // For recharge: process API response like in list-of-transfer.tsx
              const apiResponseItem = isRecharge ? response.data.results[0] : response.data[0];
              const { cleanPhoneNumber, formattedCountryCode } = cleanPhoneData(inputValues.phone_number!, inputValues.country_code!);

              const updatedItem: Record<string, unknown> = {
                ...item,
                ...inputValues,
                phone_number: cleanPhoneNumber,
                country_code: formattedCountryCode,
                phone_number_with_country_code: `${formattedCountryCode} ${cleanPhoneNumber}`,
                amount: inputValues.amount
              };

              // Process API response data like in list-of-transfer.tsx
              if (apiResponseItem) {
                // Extract telecom o  perator name from nested data object
                if (apiResponseItem.data?.name) {
                  updatedItem.telecom_name = apiResponseItem.data.name;
                }

                // Update wallet type display format
                if (apiResponseItem.wallet_type) {
                  updatedItem.wallet_type = apiResponseItem.wallet_type === 'etopup' ? 'eTop Up' : 'Digital Wallet';
                }

                // Update the status based on API response
                updatedItem.status = apiResponseItem.status;
                // Validate recharge amount against MNO rules and set error message
                if (apiResponseItem.data?.min_recharge_amount && apiResponseItem.data?.max_recharge_amount) {
                  const amount = Number(updatedItem.amount);
                  const minAmount = apiResponseItem.data.min_recharge_amount;
                  const maxAmount = apiResponseItem.data.max_recharge_amount;

                  if (amount < minAmount || amount > maxAmount) {
                    updatedItem.message = 'Min/max recharge amount criteria not met (MNO rule)';
                    updatedItem.status = 'invalid'; // Override API status for amount validation failure
                  } else {
                    updatedItem.message = '-'; // Clear error message if validation passes
                  }
                } else {
                  updatedItem.message = '-'; // Clear error message if no validation rules
                }
              }

              return updatedItem;
            } else {
              // For wallet transfer: use clean user code and display values
              const apiResponseItem = response.data[0];

              return {
                ...item,
                ...inputValues, // inputValues already contains display format
                amount: inputValues.amount,
                // Update status from API response (should be 'valid' for successful wallet transfers)
                status: apiResponseItem?.status || 'valid',
                // Update error message from API response
                error_message: apiResponseItem?.error_message || '-'
              };
            }
          }
          return item;
        });

        dispatch(setStaticData(updatedData));
      }
      setShowConfirmModal(false);
      const successMessage = isRecharge ? 'Recharge details updated successfully.' : 'Wallet transfer updated successfully!';
      dispatch(showToast({ type: 'success', message: successMessage }));
      const listType = isRecharge ? 'recharge' : 'wallet-transfer'
      // Don't pass responseData - let list screen use existing Redux state with all records preserved
      navigate(`/bulk-transfer/list-of-transfer?type=${listType}`);
      // Close modal and navigate back to list screen

    } catch (error) {
      console.error('Update failed:', error);
      dispatch(showToast({ type: 'error', message: 'Update failed. Please try again.' }));
    } finally {
      setModalLoading(false);
    }
  };

  // Dynamic inputs based on data type
  const walletInputs = {
    "": {
      "User Code": {
        type: "dropDown",
        id: "user_code",
        required: true,
        tabIndex: 2,
        disabled: false,
        placeholder: "Select user code",
        options: userOptions,
        noSearch: false,
        extraSpaceBelow: true,
        loading: loading,
        onSearch: (searchValue: string) => {
          setLoading(true);
          fetchChildAccounts(searchValue).finally(() => {
            setLoading(false);
          });
        }
      },
      "Wallet Type": {
        type: "dropDown",
        id: "wallet_type",
        required: true,
        tabIndex: 2,
        disabled: false,
        placeholder: "Select wallet type",
        options: ["eTop Up", "Digital Wallet"],
        noSearch: true,
        extraSpaceBelow: true
      },
      "Amount": {
        type: "currency",
        id: "amount",
        required: true,
        tabIndex: 3,
        disabled: false,
        placeholder: "Enter amount",
        min: 0,
        max: 999999, // Allow larger amounts
        extraSpaceBelow: true
      }
    }
  };

  const rechargeInputs = {
    "": {
      "Customer Name": {
        type: "input",
        id: "name",
        required: true,
        tabIndex: 0,
        disabled: false,
        placeholder: "Enter customer name"
      },
      "Phone Number": {
        type: "phone_number_no_verify",
        id: "phone_number",
        required: true,
        tabIndex: 1,
        disabled: false,
        placeholder: "Enter phone number",
        extraSpaceBelow: true
      },
      "Telecom": {
        type: "input",
        id: "telecom_name",
        required: false,
        tabIndex: 2,
        disabled: true,
        placeholder: "Telecom not found",
        min: 0
      },
      "Recharge Amount": {
        type: "currency",
        id: "amount",
        required: true,
        tabIndex: 3,
        disabled: false,
        placeholder: "Enter recharge amount",
        min: 0,
        max: 999999, // Allow larger recharge amounts
        extraSpaceBelow: true
      },
      "Father's Name": {
        type: "input",
        id: "father_name",
        required: false,
        tabIndex: 4,
        disabled: false,
        placeholder: "Enter father name"
      },
      "Company Name": {
        type: "input",
        id: "company_name",
        required: false,
        tabIndex: 5,
        disabled: false,
        placeholder: "Enter company name"
      },
      "Location": {
        type: "input",
        id: "location",
        required: false,
        tabIndex: 6,
        disabled: false,
        placeholder: "Enter location"
      },

      
      "Wallet Type": {
        type: "dropDown",
        id: "wallet_type",
        required: true,
        tabIndex: 7,
        disabled: false,
        placeholder: "Select wallet type",
        options: ["eTop Up", "Digital Wallet"],
        noSearch: true,
        extraSpaceBelow: true
      }
    }
  };

  const inputs = isRecharge ? rechargeInputs : walletInputs;

  const getConfirmationText = () => {
    if (isRecharge) {
      // Format phone number properly for display
      const { cleanPhoneNumber, formattedCountryCode } = cleanPhoneData(inputValues.phone_number, inputValues.country_code);
      const formattedPhone = `${formattedCountryCode} ${cleanPhoneNumber}`;
      return `You're about to update the details for ${inputValues.name} (Phone: ${formattedPhone}) with a recharge amount of AFA ${(Number(inputValues.amount) || 0).toFixed(2)}. Are you sure you want to proceed?`;
    } else {
      return `You're about to update the wallet transfer for ${inputValues.user_code} with an amount of AFA ${(Number(inputValues.amount) || 0).toFixed(2)}. Are you sure you want to proceed?`;
    }
  };
  return (
    <div>
      <Breadcrumbs
        navPath={isRecharge
          ? ["Bulk Transfer", "Recharge", "List of Recharge Bulk Transfer", "Update Recharge"]
          : ["Bulk Transfer", "Wallet Transfer", "List of Wallet Bulk Transfer", "Update Wallet Transfer"]
        }
        heading={isRecharge ? "Update Recharge" : "Update Wallet Transfer"}
        noNavigation
        links={isRecharge
          ? ["/bulk-transfer?type=recharge", "/bulk-transfer?type=recharge", "/bulk-transfer/list-of-transfer?type=recharge", "/bulk-transfer/edit-wallet"]
          : ["/bulk-transfer?type=wallet-transfer", "/bulk-transfer?type=wallet-transfer", "/bulk-transfer/list-of-transfer?type=wallet-transfer", "/bulk-transfer/edit-wallet"]
        }
        hasAddPrivilege={true}
      />
      <AddModel>
        <AddInputs
          inputs={inputs}
          totalInputCount={isRecharge ? 8 : 3}
          inputValues={inputValues}
          onInputChange={handleInputChange}
          errors={errors}
          customWidth={true}
          mapping={true}
          loadMoreOptions={async (searchValue: string, isPageChange: boolean) => {
            if (!isRecharge) {
              if (isPageChange && hasMoreUsers) {
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                return await fetchChildAccounts(searchValue, nextPage, true);
              } else if (!isPageChange) {
                // Initial load or search - set loading
                setLoading(true);
                try {
                  return await fetchChildAccounts(searchValue, 1, false);
                } finally {
                  setLoading(false);
                }
              }
            }
            return { data: { total_count: 0 } };
          }}
        />
      </AddModel>

      <AddBottomButton
        buttonName={"Update"}
        onClick={handleSubmit}
        loading={loading}
        buttonClass={"whitespace-nowrap"}
        buttonWidth={"w-[130px]"}
      />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <CustomModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Confirm Update"
          subText={getConfirmationText()}
          onHandleClose={() => setShowConfirmModal(false)}
          primaryButtonText="Update"
          secondaryButtonText="Cancel"
          handelSubmit={handleConfirmUpdate}
          buttonLoading={modalLoading}
          buttonWidth="w-[108px]"
        />
      )}
    </div>
  );
}